import { atom } from "jotai";
import { kInterestedTopics } from "./budget-constants";
import {
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
} from "date-fns";
import { kBudgetConfig, kBudgetStyles } from "./budget-translations";

export interface TabsApi {
  scrollNext: () => void;
  scrollPrev: () => void;
  scrollTo: (index: number) => void;
  currentIndex: number;
  totalTabs: number;
  canScrollNext: boolean;
  canScrollPrev: boolean;
  activeValue: string;
}

export interface BudgetTranslationStrings {
  // Error messages
  kErrorMessage: string;

  // Disclaimers
  kBudgetChatDisclaimer: string;
  kBudgetChatOldDataDisclaimer: string;

  // Page titles and headers
  kInterestedTopicsTitle: string;
  kInterestedTopicsSubtitle: string;

  // Buttons
  kNextButton: string;
  kInterestedTopicsButton: string;

  // Breadcrumbs
  kBudgetBreadcrumb: string;
  kPickStyleBreadcrumb: string;
  kInterestedTopicsBreadcrumb: string;
  kYourSummaryBreadcrumb: string;

  // Response page content
  kRelevantReadsTitle: string;
  kRelevantNewsStacksTitle: string;
  kExploreMoreTitle: string;
  kResponseOptionsTitle: string;
  kResponseGoBack: string;
  kResponseGoLanding: string;
  kResponsePickAnotherStyle: string;
  kLoadingText: string;
}

export const selectedStyleAtom = atom("");
export const interestedTopicsAtom = atom<string[]>([]);
export const viewedTopicsAtom = atom<string[]>([]);
export const tabsApiAtom = atom<TabsApi>({} as TabsApi);
export const tabsIndexAtom = atom(0);
export const currentStyleAtom = atom<string | null>(null as string);
export const tabDirectionAtom = atom(0);

export const messagesAtom = atom([]);

export interface BudgetChatMessageProps {
  messageId: number;
  createdAt: string;
  content: string[];
  topics: string[];
  type: BudgetChatMessageType;
}

export interface BudgetChatProps {
  id: String;
  messages: BudgetChatMessageProps[];
}

export enum BudgetChatMessageType {
  QUERY = "query",
  RESPONSE = "response",
  QUERY_ERROR = "query_error",
  RESPONSE_ERROR = "response_error",
  LOADING = "loading",
}

export function toIsoLocalTime() {
  const now = new Date();
  const nowIsoString = new Date(
    now.getTime() + 8 * 60 * 60 * 1000
  ).toISOString();

  return nowIsoString;
}

export function generatePayload(
  messageId: number,
  content: string | string[],
  type: BudgetChatMessageType,
  topics: string[]
) {
  const now = toIsoLocalTime();
  const payload: BudgetChatMessageProps = {
    messageId: messageId,
    createdAt: now,
    content: Array.isArray(content) ? content : [content],
    type: type,
    topics: topics,
  };

  return payload;
}

export function generateLoadingPayload(messageId: number) {
  return generatePayload(messageId, "", BudgetChatMessageType.LOADING, []);
}

export function generateTopicContentPayload(
  topics: string[],
  messageId: number,
  selectedStyle: string
) {
  const now = toIsoLocalTime();
  if (!topics.length || !selectedStyle) {
    return generatePayload(
      messageId,
      kBudgetConfig(selectedStyle).kErrorMessage,
      BudgetChatMessageType.RESPONSE_ERROR,
      topics
    );
  }

  const selectedTopics = Object.entries(kInterestedTopics)
    .filter(([key, _]) => topics.includes(key))
    .map(([_, value]) => ({
      emoji: value.emoji,
      title: value.title[selectedStyle],
      content: value.content[selectedStyle],
    }));

  const payload: BudgetChatMessageProps = {
    messageId: messageId,
    createdAt: now,
    content: selectedTopics.map(
      (topic) => `<h4>${topic.emoji} ${topic.title}</h4>${topic.content}`
    ),
    type: BudgetChatMessageType.RESPONSE,
    topics: topics,
  };

  return payload;
}

export function generateTopicTitlePayload(
  topics: string[],
  messageId: number,
  selectedStyle: string
) {
  if (!topics.length || !selectedStyle) {
    return generatePayload(
      messageId,
      kBudgetConfig(selectedStyle).kErrorMessage,
      BudgetChatMessageType.QUERY_ERROR,
      topics
    );
  }

  const selectedTopics = Object.entries(kInterestedTopics)
    .filter(([key, _]) => topics.includes(key))
    .map(([_, value]) => ({
      emoji: value.emoji,
      title: value.title[selectedStyle],
    }));

  const now = toIsoLocalTime();

  const payload: BudgetChatMessageProps = {
    messageId: messageId,
    createdAt: now,
    content: selectedTopics.map((topic) => `${topic.emoji} ${topic.title}`),
    type: BudgetChatMessageType.QUERY,
    topics: topics,
  };

  return payload;
}

/// Returns all the queries
export function getQueries(messages: BudgetChatMessageProps[]) {
  if (!messages.length) return [];

  return messages.filter(
    (message) => message.type == BudgetChatMessageType.QUERY
  );
}

/// Returns all the responses
export function getResponses(messages: BudgetChatMessageProps[]) {
  if (!messages.length) return [];

  return messages.filter(
    (message) => message.type == BudgetChatMessageType.RESPONSE
  );
}

/// Returns all the possible stack ids of a given style
/// If [priorityTopics] is provided, the stack ids of the topics will be placed
/// at the front of the list
export function getStackIDs(style: string, priorityTopics?: string[]) {
  var stackIds = [];
  var topicWithStackIds = [];

  topicWithStackIds = Object.entries(kInterestedTopics).filter(([_, topic]) =>
    Object.keys(topic).includes("stackIds")
  );

  if (priorityTopics) {
    const priorityItems = topicWithStackIds.filter(([key]) =>
      priorityTopics.includes(key)
    );
    const nonPriorityItems = topicWithStackIds.filter(
      ([key]) => !priorityTopics.includes(key)
    );
    topicWithStackIds = [...priorityItems, ...nonPriorityItems];
  }

  stackIds = topicWithStackIds.flatMap(
    ([, topic]) => topic.stackIds?.[style] ?? []
  );

  return stackIds;
}

export function getMessagesWithId(
  messages: BudgetChatMessageProps[],
  messageId: string
): BudgetChatMessageProps[] {
  if (!messages.length) return [];

  return messages.filter(
    (message) =>
      message.type == BudgetChatMessageType.RESPONSE &&
      message.messageId.toString() == messageId
  );
}

export function getExistingTopicMessageId(
  topicKeys: string[],
  messages: BudgetChatMessageProps[]
): number | null {
  const queries = getQueries(messages);
  const existingMessage = queries
    /// Avoid side effects by creating a copy
    .slice()
    .reverse()
    .find(
      (message) =>
        JSON.stringify(message.topics.sort()) ===
        JSON.stringify(topicKeys.sort())
    );

  return existingMessage ? existingMessage.messageId : null;
}

/// Given a list of [viewedTopics], returns n number of [items] that are not
/// part of the [viewedTopics] list
export function randomiseTopics(
  viewedTopics: string[],
  selectedStyle: string,
  items?: number
) {
  const available = Object.entries(kInterestedTopics)
    .filter(([key, value]) => !viewedTopics.includes(key))
    .map(([key, value]) => ({
      key: key,
      id: value.id,
      emoji: value.emoji,
      title: value.title[selectedStyle],
    }));

  return getRandomItems(available, items);
}

/// Sorts an given [array] and returns n number of [items]
export function getRandomItems(array: any[], items?: number) {
  const temp = [...array];

  /// Fisher-Yates sorting algorithm
  for (let i = temp.length - 1; i > 0; i--) {
    const maxRange = Math.floor(Math.random() * (i + 1));
    [temp[i], temp[maxRange]] = [temp[maxRange], temp[i]];
  }

  return temp.slice(0, items);
}
export function distributeItems(allItems, maxRows, minItemsPerRow = 1) {
  // Determine number of rows needed
  const rows =
    minItemsPerRow > 1
      ? Math.min(Math.ceil(allItems.length / minItemsPerRow), maxRows)
      : Math.min(allItems.length, maxRows);

  // Calculate items per row
  const result = [];
  const itemsPerRow = Math.ceil(allItems.length / rows);

  for (let i = 0; i < rows; i++) {
    const start = i * itemsPerRow;
    const end = Math.min(start + itemsPerRow, allItems.length);

    if (start < allItems.length) {
      result.push(allItems.slice(start, end));
    }
  }

  return result;
}

/// Returns a timeago text based on the news pubDate
export function shortTimeAgo(date: Date): string {
  const mins = differenceInMinutes(new Date(), date);

  if (mins < 60) return `${mins}m`;

  const hours = differenceInHours(new Date(), date);
  if (hours < 24) return `${hours}h`;

  const days = differenceInDays(new Date(), date);
  return `${days}d`;
}

/// Returns a boolean representing whethere the given [style] exists in the list of topics
export function containsStyle(style: string | string[]): boolean {
  if (!style) return false;

  return Object.keys(kBudgetStyles).includes(style as string);
}

export function existingTopics(
  topicKeys: string | string[],
  limit?: number
): string[] {
  return Object.entries(kInterestedTopics)
    .filter(([key, _]) => topicKeys.includes(key))
    .map(([key, value]) => key)
    .slice(0, limit);
}

export function convertTopicsToParams(
  topics: string | string[],
  limit?: number
): string[] {
  const topicsList = existingTopics(topics).slice(0, limit);

  return topicsList.map((topic) => topic.toString());
}

export function generateTopicsParams(topicKeys: string[], delimiter?: string) {
  if (!topicKeys || !topicKeys.length) return;

  return topicKeys
    .map((topic) => `${!delimiter ? "&" : `${delimiter}`}topic=${topic}`)
    .join("");
}
